/**
 * CSRF double-submit token reader.
 *
 * Architecture refs:
 *   §4.25 — Security baselines. The server issues a `__Host-csrf` cookie
 *           (see `apps/api/src/middleware/csrf.ts`); the client echoes its
 *           value in the `X-CSRF-Token` header on every mutating request.
 *
 * The cookie carries the `__Host-` prefix and is NOT `HttpOnly` by design —
 * it has to be readable from JavaScript for the double-submit pattern to
 * work. The session cookie is a separate HttpOnly cookie that the browser
 * attaches automatically.
 *
 * This module is deliberately tiny so it can be imported from both the
 * auth client and the API client without pulling in heavier dependencies.
 */

const CSRF_COOKIE_NAME = "__Host-csrf";
const CSRF_COOKIE_NAME_DEV = "csrf";

/**
 * Read the current CSRF token from `document.cookie`.
 *
 * Returns the token (already URL-decoded) or `null` if the cookie is
 * missing. The caller attaches it as `X-CSRF-Token` on mutating requests;
 * the server middleware verifies the header matches the cookie.
 *
 * Dev fallback: on `http://` origins the `__Host-` prefix is invalid and
 * the server issues the cookie without the prefix. We check both names so
 * local development works out of the box.
 */
export function getCsrfHeader(): string | null {
  if (typeof document === "undefined") return null;
  return (
    readCookie(document.cookie, CSRF_COOKIE_NAME) ??
    readCookie(document.cookie, CSRF_COOKIE_NAME_DEV)
  );
}

function readCookie(cookieHeader: string, name: string): string | null {
  for (const part of cookieHeader.split(";")) {
    const eq = part.indexOf("=");
    if (eq < 0) continue;
    const key = part.slice(0, eq).trim();
    if (key !== name) continue;
    const value = part.slice(eq + 1).trim();
    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  }
  return null;
}
